/** Lifecycle state of a background activity. */
export enum ActivityStatus {
    Pending = "pending",
    Running = "running",
    Completed = "completed",
    Failed = "failed"
}

/** UI renderer key used by activityUIRegistry to pick the correct view component. */
export enum ActivityUIKind {
    AsyncTask = "async_task",
    CIModelChange = "continuous_improvement_model_change"
}

/**
 * UI-ready representation of a background activity.
 *
 * This is the normalized shape that every ActivitySource must produce,
 * decoupled from any backend-specific data model (Firestore, PubSub, etc).
 * Components like ActivityItem and ActivityTray render exclusively from this.
 */
export interface ActivityViewData {
    id: string;
    kind: ActivityUIKind;
    /** Optional context shown above the title (e.g. project or dataset name). */
    contextLabel?: string;
    title: string;
    description: string | null;
    status: ActivityStatus;
    /** FontAwesome icon class (e.g. "far fa-cog"). */
    icon: string;
    /** URL the user can navigate to when the activity completes (e.g. the new dataset page). */
    targetUrl: string | null;
    openInNewTab: boolean;
    progress: { current: number; total: number } | null;
    /** Human-readable error message, only present when status is "failed". */
    error: string | null;
    createdAt: Date;
    completedAt: Date | null;
    /**
     * Whether the activity has been read by the user (no unread dot when true).
     * The source maps backend state (e.g. ActivityEventInteraction.ackAt) to this boolean.
     */
    read?: boolean;
    /** When true the item is pinned to the top of the list and shows a warning icon. */
    urgent?: boolean;
}

/**
 * Contract for any backend that provides activities to the UI.
 *
 * Implementations must expose a reactive subscribe method so the hook can
 * merge multiple sources into a single sorted list. Each source is responsible
 * for converting its backend-specific data into ActivityViewData.
 *
 * @see AsyncTaskService for the Firestore-backed implementation.
 */
export interface ActivitySource {
    subscribe(callback: (activities: ActivityViewData[]) => void): () => void;
}
